import { type EligibilityContext } from './eligibility';
import { type QueuedModalId } from './ids';

/**
 * Build-variant metadata (design D6, F5/F6): packages never read the build
 * profile; they *declare* it and composition filters. The on-prem shell
 * aggregates only `'all'` contributions; the cloud shell aggregates both.
 *
 * Omitted = `'cloud-only'` (R2 sign-off item 2 draft: a forgotten declaration
 * withholds a modal from on-prem — visible in the manifest diff — rather than
 * leaking a cloud surface into the airgapped build).
 */
export type ContributionBuilds = 'all' | 'cloud-only';

/**
 * Framework-neutral lazy pointer at the implementation in `ui/`. Composition
 * wraps it in `next/dynamic`; the chunk loads only when the surface actually
 * renders (§5.4 property 3 — eligibility never loads the component).
 */
export type ContributionLoadThunk = () => Promise<{ default: unknown }>;

/** Rejects extra keys at the call site while preserving literal inference. */
type ExactShape<T, Shape> = Exclude<keyof T, keyof Shape> extends never ? T : never;

/**
 * One discovered `./contributions` door, as emitted by the generated registry
 * (`src/composition/registry.gen.ts`, task G1). The door's exports are
 * deliberately typed `unknown`: discovery is mechanical and must not assume a
 * shape, so aggregation narrows each export through the descriptor guards and
 * reports the owning `package` when one does not conform.
 */
export interface ContributionModule {
  /** Package name as imported, e.g. `@heidi/growth`. */
  readonly package: string;
  /** The namespace object of that package's `./contributions` door. */
  readonly module: Record<string, unknown>;
}

/**
 * A queued launch modal/spotlight/coachmark declaration (design D3).
 *
 * `id`, `dismissible`, `showOnce`, `showOnlyAfterAccountCreation` carry over
 * verbatim from the legacy `QueuedModal`; `priority` is a globally unique
 * frozen number (design D5 — migrated entries use the script-assigned values
 * in `openspec/changes/monorepo-shell-registry/frozen-priorities.md`; new
 * entries pick any unused value under the T0–T5 review process).
 *
 * Deliberately absent in v1 (reserved, design D5/D11): `band` (§5.4 ordering
 * ranges — deferred, team-owned, post-migration) and `tier` (the live T0–T5
 * *eligibility* framework keeps its id-keyed app-side register through its
 * rollout; the field joins at its register-totality phase, task Z5). The
 * pacing exempt list is transitional and never becomes a field (D11).
 *
 * v1 covers **launch** surfaces (the config-atom population). User-triggered
 * modals keep their imperative enqueue-by-id path; their registration joins
 * this door additively when the first such flip needs it (design D3b).
 */
export interface QueuedModalContribution {
  /**
   * Discriminant, stamped by {@link defineQueuedModal} — authors never write
   * it. Settings and nav descriptors are otherwise structurally identical, and
   * aggregation walks every `./contributions` export without knowing how a
   * domain organized its files, so the kind has to travel on the descriptor.
   */
  readonly kind: 'queued-modal';
  /** Dual-regime id (D4). Frozen for existing surfaces — never renamed. */
  readonly id: QueuedModalId;
  /**
   * Globally unique frozen number encoding the queue's effective total order.
   * Same-id multi-priority sites (`CLINICIAN_TRIAL`) are R2 sign-off item 3.
   */
  readonly priority: number;
  /** Can be dismissed by clicking outside / ESC. */
  readonly dismissible?: boolean;
  /**
   * Show once, tracked via backend `seenModalIds` — requires a `ModalId` enum
   * id (never a local-regime id; design D4).
   */
  readonly showOnce?: boolean;
  /** Only show after account creation (new-user route visited this session). */
  readonly showOnlyAfterAccountCreation?: boolean;
  /**
   * Synchronous, tiny launch-eligibility predicate — runs at startup against
   * the composition-populated context, before any chunk loads (D3a). Must
   * handle "not yet loaded" via the context's readiness fields.
   */
  readonly eligible: (context: EligibilityContext) => boolean;
  readonly load: ContributionLoadThunk;
  readonly builds?: ContributionBuilds;
}

/**
 * A settings screen declaration: placement metadata + lazy implementation
 * pointer. `section`/`order` are validated against the composition-owned slot
 * map (§5.4 — composition owns which sections exist and their order bands,
 * never the member list). Display metadata (label/icon) joins additively with
 * the first consumer flip (F2).
 */
export interface SettingsScreenContribution {
  /** Discriminant, stamped by {@link defineSettingsScreen}. */
  readonly kind: 'settings-screen';
  readonly id: SettingsScreenContributionId;
  readonly section: string;
  readonly order: number;
  readonly load: ContributionLoadThunk;
  readonly builds?: ContributionBuilds;
}

/**
 * The id a settings-screen descriptor claims — the settings route slug the
 * screen is served at.
 *
 * `string` here on purpose. The vocabulary of settings routes is the app's
 * information architecture, and a platform package that enumerated it would own
 * a list it cannot see and would have to be edited every time a route appeared
 * — the central file this door exists to remove. So the door types the shape
 * and the app narrows it: composition intersects this with its own slug union
 * where a wrong id has to be a compile error rather than a silent no-op
 * (`src/composition/settings-screen-hosts.ts`).
 *
 * It is a named alias rather than an inline `string` so that the app-side
 * narrowing has something to be pinned to: if this ever becomes a union, every
 * intersection over it narrows with it instead of quietly widening.
 */
export type SettingsScreenContributionId = string;

/**
 * A navigation entry declaration. Same shape and slot-map rules as settings
 * screens. This is a descriptor, not a bypass: a NEW top-level IA entry
 * remains a design-owned decision (the sidebar-IA rule) — the contract types
 * the declaration; it does not grant the slot.
 */
export interface NavEntryContribution {
  /** Discriminant, stamped by {@link defineNavEntry}. */
  readonly kind: 'nav-entry';
  readonly id: string;
  readonly section: string;
  readonly order: number;
  readonly load: ContributionLoadThunk;
  readonly builds?: ContributionBuilds;
}

/**
 * A feature-flag declaration: the domain-owned record of a flag's identity and
 * its code default (design D10 — this is the `contributions/feature-flags.ts`
 * *contract*, not a migration mandate). Declaring moves the key/default pair's
 * source of truth into the owning domain; *reading* stays app-side — flags
 * arrive in the app's provider and reach descriptors through the eligibility
 * context (D3a), so a contributions file still never imports a flag getter.
 */
export interface FeatureFlagContribution {
  /** Discriminant, stamped by {@link defineFeatureFlag}. */
  readonly kind: 'feature-flag';
  /**
   * The external system's key — Flagsmith/PostHog target the flag by it, so it
   * is frozen: renaming the key IS switching to a different flag, never a
   * refactor.
   */
  readonly key: string;
  /**
   * The code default: what every read returns when no provider resolves the
   * key (on-prem runs without a flag provider, so this is the on-prem value).
   */
  readonly defaultValue: boolean;
  readonly description?: string;
  readonly builds?: ContributionBuilds;
}

/**
 * Phantom carrier for an analytics event's payload type.
 *
 * The key is a module-private `unique symbol` declared with no runtime value,
 * so the property exists only in the type system: a descriptor never carries a
 * payload at runtime, and no consumer can read one by mistake because the
 * symbol is not exported. {@link AnalyticsEventProps} is the sanctioned way
 * back out.
 */
declare const ANALYTICS_PAYLOAD: unique symbol;

export interface AnalyticsPayloadOf<Props> {
  readonly [ANALYTICS_PAYLOAD]?: Props;
}

/** The payload type an analytics-event descriptor was declared with. */
export type AnalyticsEventProps<T> =
  T extends AnalyticsPayloadOf<infer Props> ? Props : never;

/**
 * A frozen analytics event-name declaration (design D9, ruling 27).
 *
 * The event NAME is the whole point: it is the string a warehouse schema is
 * keyed on, so two spellings are two events and a rename is a new event rather
 * than a refactor. D9 homes each name in exactly one place — the owning
 * package's `contributions/analytics.ts` — and app-side emitters read
 * `descriptor.name` instead of re-typing the literal.
 *
 * The PAYLOAD type stays in `contracts` (the `*-analytics.types.ts` pattern
 * ratified by ruling 24) and reaches the descriptor as a TYPE-ONLY import; it
 * travels on the phantom above rather than as a field, so the descriptor stays
 * pure metadata for the manifest and the featherweight budget.
 *
 * Deliberately absent: the emit. A package declares its events and never
 * imports a tracker — emission is an app reaction supplied at the composition
 * point (ruling 24), and this descriptor does not reopen that.
 */
export interface AnalyticsEventContribution<
  Props = unknown,
> extends AnalyticsPayloadOf<Props> {
  /** Discriminant, stamped by {@link defineAnalyticsEvent}. */
  readonly kind: 'analytics-event';
  /**
   * The frozen snake_case event name, e.g. `work_surface_actioned`. Unique
   * across every package — aggregation reports `duplicate-event-name`, and the
   * event-reference gate's third arm reports the same collision from source.
   */
  readonly name: string;
  readonly description?: string;
  readonly builds?: ContributionBuilds;
}

/** Anything a `./contributions` door may legally export. */
export type ShellContribution =
  | QueuedModalContribution
  | SettingsScreenContribution
  | NavEntryContribution
  | FeatureFlagContribution
  | AnalyticsEventContribution;

/** What an author writes — the `kind` discriminant is stamped for them. */
type ContributionInput<T extends ShellContribution> = Omit<T, 'kind'>;

/**
 * Analytics events drop the phantom payload key as well: the author writes
 * neither it nor `kind`, and leaving it in the input would bind it to
 * `unknown` before the caller's `Props` could reach it.
 */
type AnalyticsEventInput = Omit<
  AnalyticsEventContribution,
  'kind' | typeof ANALYTICS_PAYLOAD
>;

/*
 * The define* helpers stamp the discriminant and return the descriptor
 * unchanged otherwise: pure data — no state, no registration, no side effects.
 * Runtime's registries-stay-empty rule extends to this door: membership is
 * aggregated by app composition from the generated registry (G1), never
 * collected here. The exact-shape constraint rejects unknown keys (incl. the
 * reserved `band`/`tier`, and `kind` itself) at the call site while preserving
 * literal types for the manifest.
 */

export const defineQueuedModal = <T extends ContributionInput<QueuedModalContribution>>(
  contribution: T & ExactShape<T, ContributionInput<QueuedModalContribution>>
): T & { readonly kind: 'queued-modal' } => ({ ...contribution, kind: 'queued-modal' });

export const defineSettingsScreen = <
  T extends ContributionInput<SettingsScreenContribution>,
>(
  contribution: T & ExactShape<T, ContributionInput<SettingsScreenContribution>>
): T & { readonly kind: 'settings-screen' } => ({
  ...contribution,
  kind: 'settings-screen',
});

export const defineNavEntry = <T extends ContributionInput<NavEntryContribution>>(
  contribution: T & ExactShape<T, ContributionInput<NavEntryContribution>>
): T & { readonly kind: 'nav-entry' } => ({ ...contribution, kind: 'nav-entry' });

export const defineFeatureFlag = <T extends ContributionInput<FeatureFlagContribution>>(
  contribution: T & ExactShape<T, ContributionInput<FeatureFlagContribution>>
): T & { readonly kind: 'feature-flag' } => ({ ...contribution, kind: 'feature-flag' });

/**
 * CURRIED, and not for style. The payload type is supplied by the author and
 * the descriptor is inferred from the object literal, and TypeScript has no
 * partial type-argument inference: a single `<Props, T>` list would force the
 * call site to spell `T` out too, which widens `name` from its literal to
 * `string`. That literal is load-bearing — app-side maps built from these
 * descriptors (`WorkLaunchEventName`) are unions of it, and a widened `name`
 * silently degrades every `Record<EventName, …>` keyed on them to
 * `Record<string, …>`.
 *
 * `const T` is what keeps the literal through the intersection; the extra `()`
 * is the price of the two independent type arguments.
 */
export const defineAnalyticsEvent =
  <Props>() =>
  <const T extends AnalyticsEventInput>(
    contribution: T & ExactShape<T, AnalyticsEventInput>
  ): T & { readonly kind: 'analytics-event' } & AnalyticsPayloadOf<Props> => ({
    ...contribution,
    kind: 'analytics-event',
  });

/*
 * Guards live with the contract, not with either consumer: composition's
 * aggregation (task G2) and the contribution CI (tasks C1–C2) must agree
 * exactly on what counts as a valid descriptor, and two copies would drift.
 * They are deliberately structural — a door's exports arrive as `unknown`.
 */

const hasKind = (value: unknown, kind: ShellContribution['kind']): boolean =>
  typeof value === 'object' &&
  value !== null &&
  (value as { kind?: unknown }).kind === kind;

const isPlacement = (value: unknown): boolean => {
  const candidate = value as Partial<SettingsScreenContribution>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.section === 'string' &&
    typeof candidate.order === 'number' &&
    typeof candidate.load === 'function'
  );
};

export const isQueuedModalContribution = (
  value: unknown
): value is QueuedModalContribution => {
  if (!hasKind(value, 'queued-modal')) return false;
  const candidate = value as Partial<QueuedModalContribution>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.priority === 'number' &&
    Number.isFinite(candidate.priority) &&
    typeof candidate.eligible === 'function' &&
    typeof candidate.load === 'function'
  );
};

export const isSettingsScreenContribution = (
  value: unknown
): value is SettingsScreenContribution =>
  hasKind(value, 'settings-screen') && isPlacement(value);

export const isNavEntryContribution = (value: unknown): value is NavEntryContribution =>
  hasKind(value, 'nav-entry') && isPlacement(value);

export const isFeatureFlagContribution = (
  value: unknown
): value is FeatureFlagContribution => {
  if (!hasKind(value, 'feature-flag')) return false;
  const candidate = value as Partial<FeatureFlagContribution>;
  return typeof candidate.key === 'string' && typeof candidate.defaultValue === 'boolean';
};

export const isAnalyticsEventContribution = (
  value: unknown
): value is AnalyticsEventContribution => {
  if (!hasKind(value, 'analytics-event')) return false;
  const candidate = value as Partial<AnalyticsEventContribution>;
  return typeof candidate.name === 'string' && candidate.name.length > 0;
};

/** True for any well-formed descriptor of any kind. */
export const isShellContribution = (value: unknown): value is ShellContribution =>
  isQueuedModalContribution(value) ||
  isSettingsScreenContribution(value) ||
  isNavEntryContribution(value) ||
  isFeatureFlagContribution(value) ||
  isAnalyticsEventContribution(value);
